// Planer — wspólny stan, pobieranie i akcje wszystkich jego widoków (Dzień, Tydzień, Miesiąc,
// Zadania). Dane zakresu dni przychodzą jednym zapytaniem (/planner), a oś składa useV2Day.
// Tu dochodzi to, czego tam nie ma: zadania cykliczne i płatności z Portfela na osi, geometria
// osi z godzinami z ustawień, przestawianie przeciąganiem i odhaczanie bez okienka.
import { computed, getCurrentInstance, onScopeDispose, ref, watch } from 'vue';
import { useApi } from '~/composables/useApi.ts';
import { useV2Day, V2_KINDS, V2_FILTERS, isWorkoutDone, taskDeadline } from '@/composables/useV2Day.js';
import { useAiEntries } from '@/composables/useAiEntries.js';
import { ymd } from '@/composables/useV2Crud.js';
import { useV2Entry } from '@/composables/useV2Entry.js';
import { intlLocale } from '@/composables/useLanguage.js';
import { t } from '@/i18n/index.js';

const pad = (n) => String(n).padStart(2, '0');
export const TIMEZONE = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Warsaw'; } catch { return 'Europe/Warsaw'; } })();

// Rodzaje spoza useV2Day: kolejne dni zadania cyklicznego (tylko do podglądu) i płatność z Portfela.
export const PLANER_KINDS = {
    ...V2_KINDS,
    repeat: { get label() { return t('planner.kinds.repeat'); }, color: '#5B8C6E', soft: '#D6E4DB' },
    payment: { get label() { return t('planner.kinds.payment'); }, color: '#B5643C', soft: '#EBCDBB' },
};
export const PLANER_FILTERS = [
    // bez spreadu — getter etykiety musi zostać żywy (język zmienia się w trakcie)
    ...V2_FILTERS.map(f => (f.key === 'tasks' ? { key: f.key, get label() { return f.label; }, color: f.color, kinds: [...f.kinds, 'repeat'] } : f)),
    { key: 'payments', get label() { return t('planner.filters.payments'); }, color: PLANER_KINDS.payment.color, kinds: ['payment'] },
];
export const kindColor = (kind) => PLANER_KINDS[kind] ?? { color: '#A29A8E', soft: '#D8D2C6' };

const weekday = (iso, key) => ({
    iso,
    get short() { return t(`planner.weekdays.short.${key}`); },
    get long() { return t(`planner.weekdays.long.${key}`); },
});
export const WEEKDAYS = [
    weekday(1, 'mon'), weekday(2, 'tue'), weekday(3, 'wed'), weekday(4, 'thu'),
    weekday(5, 'fri'), weekday(6, 'sat'), weekday(7, 'sun'),
];
export const REPEAT_OPTIONS = [
    { value: 'none', get label() { return t('planner.repeat.none'); } },
    { value: 'daily', get label() { return t('planner.repeat.daily'); } },
    { value: 'weekdays', get label() { return t('planner.repeat.weekdays'); } },
    { value: 'weekly', get label() { return t('planner.repeat.weekly'); } },
    { value: 'monthly', get label() { return t('planner.repeat.monthly'); } },
];
export const REMINDER_OPTIONS = [
    { value: null, get label() { return t('planner.reminder.none'); } },
    { value: 0, get label() { return t('planner.reminder.onTime'); } },
    { value: 10, get label() { return t('planner.reminder.minutes', { n: 10 }); } },
    { value: 30, get label() { return t('planner.reminder.minutes', { n: 30 }); } },
    { value: 60, get label() { return t('planner.reminder.hour'); } },
    { value: 1440, get label() { return t('planner.reminder.day'); } },
];

// „co 2 tygodnie: pn, śr" — reguła zadania cyklicznego po ludzku.
export const repeatLabel = (rule) => {
    if (!rule?.freq) return null;
    const n = rule.interval ?? 1;
    const every = (unit) => (n > 1 ? t(`planner.repeatRule.every.${unit}`, { n }, n) : t(`planner.repeatRule.${unit}`));
    if (rule.freq === 'daily') return every('daily');
    if (rule.freq === 'weekdays') return t('planner.repeatRule.weekdays');
    if (rule.freq === 'monthly') return every('monthly');
    const days = (rule.days ?? []).map(d => WEEKDAYS[d - 1]?.short).filter(Boolean).join(', ');
    return `${every('weekly')}${days ? `: ${days}` : ''}`;
};

export const reminderLabel = (minutes) => REMINDER_OPTIONS.find(o => o.value === minutes)?.label ?? null;

// --- dni ---

export const parseDay = (value) => {
    const m = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
};
export const today0 = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
export const shiftDay = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); d.setHours(0, 0, 0, 0); return d; };
export const weekStart = (date) => shiftDay(date, -((date.getDay() + 6) % 7));
export const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
export const isoWeek = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    return Math.ceil(((d - new Date(Date.UTC(d.getUTCFullYear(), 0, 1))) / 86400000 + 1) / 7);
};
// Chwila: dzień + minuty od północy → ISO z przesunięciem strefy (API zapisuje w UTC).
export const isoAt = (day, minutes) => {
    const d = new Date(day);
    d.setHours(0, minutes, 0, 0);
    return d.toISOString().replace(/\.\d{3}Z$/, '+00:00');
};
export const minutesOf = (value) => { const d = new Date(value); return d.getHours() * 60 + d.getMinutes(); };
export const hhmm = (minutes) => `${pad(Math.floor(minutes / 60) % 24)}:${pad(minutes % 60)}`;

// Pole szybkiego dodawania: „14:30 dentysta" albo „dentysta o 14:30" → treść i minuty od północy.
export const parseQuick = (input) => {
    const raw = String(input ?? '').trim();
    const m = raw.match(/^(?:(?:o|at)\s+)?(\d{1,2})[:.](\d{2})\s+(.+)$/i) ?? raw.match(/^(.+?)\s+(?:(?:o|at)\s+)?(\d{1,2})[:.](\d{2})$/i);
    if (m) {
        const [h, min, rest] = /^\d/.test(m[1]) ? [m[1], m[2], m[3]] : [m[2], m[3], m[1]];
        if (Number(h) < 24 && Number(min) < 60) return { text: rest.trim(), minutes: Number(h) * 60 + Number(min) };
    }
    return { text: raw, minutes: null };
};

// „Wtorek 4 sierpnia" — dzień tygodnia i data osobno, bez przecinka z locale.
export const dayTitle = (d) => {
    const wd = d.toLocaleDateString(intlLocale(), { weekday: 'long' });
    return `${wd.charAt(0).toUpperCase()}${wd.slice(1)} ${d.toLocaleDateString(intlLocale(), { day: 'numeric', month: 'long' })}`;
};
export const dayRelation = (d) => {
    const diff = Math.round((d - today0()) / 86400000);
    if (diff === 0) return t('planner.relation.today');
    if (diff === -1) return t('planner.relation.yesterday');
    if (diff === 1) return t('planner.relation.tomorrow');
    if (d.getFullYear() !== new Date().getFullYear()) return String(d.getFullYear());
    return null;
};
export const rangeLabel = (from, to) => {
    const right = to.toLocaleDateString(intlLocale(), { day: 'numeric', month: 'long' });
    if (from.getMonth() === to.getMonth()) return `${from.getDate()} – ${right}`;
    return `${from.toLocaleDateString(intlLocale(), { day: 'numeric', month: 'long' })} – ${right}`;
};

// --- stan wspólny ---
const DEFAULT_SETTINGS = { day_start: 6, day_end: 23, remind_before: null, hidden: [], module_reminders: { people: true, pets: true, devices: true, habits: true, medications: true } };
const settings = ref({ ...DEFAULT_SETTINGS });
const settingsLoaded = ref(false);
const catalogs = ref({ meals: [], products: [], workoutTypes: [], checklists: [], profile: null });
const catalogsLoaded = ref(false);
const changed = ref(0);
const flash = ref(null);
let flashTimer = null;
const listeners = new Set();

// „Pokaż" — ukryte rodzaje. Wybór zapamiętuje się na tym urządzeniu; bez niego obowiązuje
// domyślny zestaw z Ustawień planera.
const HIDDEN_KEY = 'rountine_planer_hidden';
const storedHidden = () => { try { const v = JSON.parse(localStorage.getItem(HIDDEN_KEY)); return Array.isArray(v) ? v : null; } catch { return null; } };
const storeHidden = (list) => { try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(list)); } catch { /* na tę sesję */ } };
const hidden = ref(null);
const KIND_TO_FILTER = Object.fromEntries(PLANER_FILTERS.flatMap(f => f.kinds.map(k => [k, f.key])));
export const filterOf = (kind) => KIND_TO_FILTER[kind] ?? null;

// Dzień bez ukrytych rodzajów (oś, cały dzień, zadania bez godziny).
export const visibleDay = (day, hiddenKeys) => {
    const shown = (ev) => !hiddenKeys.includes(filterOf(ev.kind));
    return {
        ...day,
        events: day.events.filter(shown),
        timed: day.timed.filter(shown),
        allDay: day.allDay.filter(shown),
        unscheduled: hiddenKeys.includes('tasks') ? [] : day.unscheduled,
        repeatsWithoutTime: hiddenKeys.includes('tasks') ? [] : day.repeatsWithoutTime,
    };
};

// Liczby przy „Pokaż" — wpisy z dni widoku (zadania sztukami, z tymi bez godziny).
export const countKinds = (days) => {
    const out = Object.fromEntries(PLANER_FILTERS.map(f => [f.key, 0]));
    for (const d of days) {
        for (const ev of [...d.timed, ...d.allDay]) {
            const key = filterOf(ev.kind);
            if (key && ev.kind !== 'task') out[key] += 1;
        }
        out.tasks += d.progress.total + d.repeatsWithoutTime.length + d.timed.filter(e => e.kind === 'repeat').length;
    }
    return out;
};

// Każdy zapis w planerze — przeładowanie widoków i Pierwszych kroków.
const notify = () => { changed.value++; for (const fn of listeners) fn(); };
export const onPlanerChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

export const showFlash = (message) => {
    flash.value = message;
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => { flash.value = null; }, 4000);
};

export function usePlaner() {
    const api = useApi();
    const { createdSignal } = useAiEntries();

    const loadSettings = async () => {
        if (settingsLoaded.value) return settings.value;
        try { settings.value = { ...DEFAULT_SETTINGS, ...(await api.get('/planner/settings')) }; } catch { /* domyślne */ }
        settingsLoaded.value = true;
        hidden.value ??= storedHidden() ?? [...(settings.value.hidden ?? [])];
        return settings.value;
    };
    const hiddenKinds = computed(() => hidden.value ?? settings.value.hidden ?? []);
    const toggleHidden = (key) => {
        const now = new Set(hiddenKinds.value);
        if (now.has(key)) now.delete(key);
        else now.add(key);
        hidden.value = [...now];
        storeHidden(hidden.value);
    };
    // „Wszystkie" — nic nie jest ukryte
    const showAll = () => {
        hidden.value = [];
        storeHidden([]);
    };
    const saveSettings = async (patch) => {
        const previous = settings.value;
        settings.value = { ...previous, ...patch };
        try { settings.value = await api.put('/planner/settings', patch); } catch { settings.value = previous; showFlash(t('planner.flash.settingsFailed')); }
    };

    // Katalogi okienka dodawania — raz na sesję, w tle.
    const loadCatalogs = async (force = false) => {
        if (catalogsLoaded.value && !force) return catalogs.value;
        const safe = (path, fallback = []) => api.get(path).catch(() => fallback);
        const [meals, products, workoutTypes, checklists, profile] = await Promise.all([
            safe('/meals'), safe('/products'), safe('/workout-types'), safe('/checklists'), safe('/profile', null),
        ]);
        catalogs.value = { meals, products, workoutTypes, checklists, profile };
        catalogsLoaded.value = true;
        return catalogs.value;
    };

    // Odhaczenie zadania: stan od razu, zapis w tle. Cykliczne po odhaczeniu przechodzi na
    // następny dzień, więc przeładowujemy widok.
    const toggleTask = async (task) => {
        const previous = task.done;
        task.done = !previous;
        try {
            await api.patch(`/checklist-items/${task.uuid}`, { done: task.done, timezone: TIMEZONE });
            notify();
        } catch {
            task.done = previous;
            showFlash(t('planner.flash.taskFailed'));
        }
    };

    // Zjedzone / zaplanowane wprost z osi; API chce przy zapisie nazwy i godziny.
    const toggleMeal = async (entry) => {
        const previous = entry.status;
        entry.status = previous === 'eaten' ? 'planned' : 'eaten';
        try {
            if (entry.food) await api.patch(`/food/entries/${entry.uuid}`, { status: entry.status });
            else await api.patch(`/meal-entries/${entry.uuid}`, { name: entry.name, planned_at: entry.planned_at, status: entry.status });
            notify();
        } catch {
            entry.status = previous;
            showFlash(t('planner.flash.mealFailed'));
        }
    };

    const workoutBody = (w, patch = {}) => ({
        type: w.type?.uuid ?? w.type,
        performed_at: w.performed_at,
        status: w.status ?? 'done',
        duration_seconds: w.duration_seconds ?? null,
        active_calories: w.active_calories ?? null,
        distance_meters: w.distance_meters ?? null,
        ...patch,
    });

    // Wykonany trening potrzebuje czasu i kalorii — bez nich zwracamy false i strona otwiera okienko.
    const toggleWorkout = async (workout) => {
        const planned = !isWorkoutDone(workout);
        if (planned && (!workout.duration_seconds || workout.active_calories == null)) return false;
        const previous = workout.status ?? 'done';
        workout.status = planned ? 'done' : 'planned';
        try {
            await api.patch(`/workouts/${workout.uuid}`, workoutBody(workout));
            notify();
        } catch {
            workout.status = previous;
            showFlash(t('planner.flash.workoutFailed'));
        }
        return true;
    };

    // Przestawienie zadań jednym zapytaniem: [{ uuid, planned_for?, due_at? }].
    const reschedule = async (changes) => {
        if (!changes.length) return true;
        try {
            await api.post(`/planner/reschedule?timezone=${encodeURIComponent(TIMEZONE)}`, { tasks: changes });
            notify();
            return true;
        } catch {
            showFlash(t('planner.flash.rescheduleFailed'));
            return false;
        }
    };

    // Przeciągnięty wpis na nowy dzień i godzinę (minuty od północy; null = bez godziny).
    const move = async (ev, day, minutes) => {
        const at = minutes == null ? null : isoAt(day, minutes);
        try {
            if (ev.kind === 'task' || ev.kind === 'unscheduled') {
                return reschedule([{ uuid: ev.task.uuid, planned_for: ymd(day), due_at: at }]);
            }
            if (ev.kind === 'meal' || ev.kind === 'meal-planned') {
                if (ev.entry.food) {
                    await api.patch(`/food/entries/${ev.entry.uuid}`, { date: ymd(day), ...(minutes == null ? {} : { time: hhmm(minutes) }) });
                    notify();
                    return true;
                }
                await api.patch(`/meal-entries/${ev.entry.uuid}`, { name: ev.entry.name, planned_at: at ?? isoAt(day, minutesOf(ev.entry.planned_at)), status: ev.entry.status });
            } else if (ev.kind === 'workout') {
                await api.patch(`/workouts/${ev.workout.uuid}`, workoutBody(ev.workout, { performed_at: at ?? isoAt(day, minutesOf(ev.workout.performed_at)) }));
            } else if (ev.kind === 'event' && !ev.allDay) {
                const c = ev.calendarEvent;
                const length = new Date(c.ends_at) - new Date(c.starts_at);
                const start = at ?? isoAt(day, minutesOf(c.starts_at));
                const end = new Date(new Date(start).getTime() + length).toISOString().replace(/\.\d{3}Z$/, '+00:00');
                await api.patch(`/calendar-events/${c.uuid}`, { title: c.title, description: c.description ?? null, location: c.location ?? null, all_day: false, starts_at: start, ends_at: end });
            } else {
                return false;
            }
            notify();
            return true;
        } catch {
            showFlash(t('planner.flash.moveFailed'));
            return false;
        }
    };

    // Nowy koniec wpisu przeciągnięty dolną krawędzią: wydarzenie dostaje nowe ends_at,
    // trening — czas trwania. `end` to minuty od północy dnia wpisu.
    const resize = async (ev, end) => {
        try {
            if (ev.kind === 'event' && !ev.allDay) {
                const c = ev.calendarEvent;
                const start = new Date(c.starts_at);
                const at = new Date(start);
                at.setHours(0, end, 0, 0);
                if (at <= start) return false;
                await api.patch(`/calendar-events/${c.uuid}`, { title: c.title, description: c.description ?? null, location: c.location ?? null, all_day: false, starts_at: c.starts_at, ends_at: at.toISOString().replace(/\.\d{3}Z$/, '+00:00') });
            } else if (ev.kind === 'workout') {
                const minutes = end - minutesOf(ev.workout.performed_at);
                if (minutes < 5) return false;
                await api.patch(`/workouts/${ev.workout.uuid}`, workoutBody(ev.workout, { duration_seconds: minutes * 60 }));
            } else {
                return false;
            }
            notify();
            return true;
        } catch {
            showFlash(t('planner.flash.resizeFailed'));
            return false;
        }
    };

    // Menu wpisu: kopia (ten sam dzień i godzina), usunięcie, przeniesienie na jutro.
    const { draftFromEntry, buildRequest } = useV2Entry();
    const { editTarget } = useV2Day();
    // Kopia wpisu nowego Jedzenia: ten sam posiłek (albo szybki wpis) w tym samym dniu, jako plan.
    const foodCopy = async (e) => {
        try {
            await api.post('/food/entries', {
                date: e.date, slot: e.slot, time: e.time, status: 'planned',
                ...(e.meal ? { meal: e.meal.uuid, portions: e.portions, choices: e.choices } : { name: e.name, emoji: e.emoji, nutrition: e.values, items: e.items ?? [] }),
            });
            notify();
            showFlash(t('planner.flash.copied'));
            return true;
        } catch {
            showFlash(t('planner.flash.copyFailed'));
            return false;
        }
    };
    const duplicate = async (ev) => {
        if (ev.entry?.food) return foodCopy(ev.entry.food);
        const target = editTarget(ev);
        if (!target?.entry) return false;
        const draft = { ...draftFromEntry(target.type, target.entry), repeat: 'none' };
        const { url, body } = buildRequest(target.type, draft, null);
        try {
            await api.post(url, body);
            notify();
            showFlash(t('planner.flash.copied'));
            return true;
        } catch {
            showFlash(t('planner.flash.copyFailed'));
            return false;
        }
    };
    const remove = async (ev) => {
        if (ev.entry?.food) {
            try {
                await api.delete(`/food/entries/${ev.entry.uuid}`);
                notify();
                return true;
            } catch {
                showFlash(t('planner.flash.removeFailed'));
                return false;
            }
        }
        const target = editTarget(ev);
        if (!target?.entry) return false;
        const { url } = buildRequest(target.type, draftFromEntry(target.type, target.entry), target.entry);
        try {
            await api.delete(url);
            notify();
            return true;
        } catch {
            showFlash(t('planner.flash.removeFailed'));
            return false;
        }
    };
    const tomorrow = (ev) => {
        const day = shiftDay(ev.at, 1);
        const minutes = ev.kind === 'event' && ev.calendarEvent ? minutesOf(ev.calendarEvent.starts_at) : minutesOf(ev.at);
        return move(ev, day, ev.kind === 'task' && !ev.task.due_at ? null : minutes);
    };

    // Płatność z Portfela odhaczona w planerze — zapisuje się jako transakcja w Portfelu.
    const payPayment = async (occurrence) => {
        if (!occurrence.plan?.uuid) return false;
        try {
            await api.post(`/wallet/plans/${occurrence.plan.uuid}/pay`, { period: occurrence.period });
            notify();
            return true;
        } catch {
            showFlash(t('planner.flash.paymentFailed'));
            return false;
        }
    };

    // Szybkie dodanie zadania z pola: „14:30 dentysta" → zadanie z godziną.
    const quickTask = async (text, day) => {
        const { text: content, minutes } = parseQuick(text);
        if (!content) return false;
        try {
            await api.post('/checklist-items', {
                text: content,
                planned_for: day ? ymd(day) : null,
                due_at: day && minutes != null ? isoAt(day, minutes) : null,
                remind_before: day && minutes != null ? settings.value.remind_before : null,
                timezone: TIMEZONE,
            });
            notify();
            return true;
        } catch {
            showFlash(t('planner.flash.addTaskFailed'));
            return false;
        }
    };

    return {
        api, settings, catalogs, changed, flash, createdSignal, hiddenKinds, toggleHidden, showAll,
        loadSettings, saveSettings, loadCatalogs, notify,
        toggleTask, toggleMeal, toggleWorkout, reschedule, move, resize, duplicate, remove, tomorrow, payPayment, quickTask,
    };
}

// Które Jedzenie pokazuje planer (pole `food` z /planner): „new" = posiłki dodaje się w nowym module.
export const foodVersion = ref('old');

// Dane zakresu dni z /planner; przeładowanie po każdym zapisie w planerze i po dyktowaniu.
export function usePlanerRange(range) {
    const api = useApi();
    const { createdSignal } = useAiEntries();
    const data = ref(null);
    const loading = ref(true);
    const error = ref(false);
    let seq = 0;

    const load = async () => {
        const { from, to } = range.value;
        const mine = ++seq;
        error.value = false;
        try {
            const result = await api.get('/planner', { query: { from: ymd(from), to: ymd(to), timezone: TIMEZONE } });
            if (mine === seq) {
                data.value = result;
                foodVersion.value = result.food ?? 'old';
            }
        } catch {
            if (mine === seq) error.value = !data.value;
            if (data.value) showFlash(t('planner.flash.refreshFailed'));
        } finally {
            if (mine === seq) loading.value = false;
        }
    };

    watch(() => `${ymd(range.value.from)}/${ymd(range.value.to)}`, load, { immediate: true });
    watch(createdSignal, load);
    const off = onPlanerChange(load);
    if (getCurrentInstance()) onScopeDispose(off);

    return { data, loading, error, reload: load };
}

// Kropki mini-kalendarza — lekkie /planner/days na miesiąc.
export function usePlanerDays(month) {
    const api = useApi();
    const days = ref({});
    const load = async () => {
        const from = startOfMonth(month.value);
        const to = new Date(from.getFullYear(), from.getMonth() + 1, 0);
        try { days.value = await api.get('/planner/days', { query: { from: ymd(shiftDay(from, -7)), to: ymd(shiftDay(to, 7)), timezone: TIMEZONE } }); } catch { /* bez kropek */ }
    };
    watch(() => `${month.value.getFullYear()}-${month.value.getMonth()}`, load, { immediate: true });
    const off = onPlanerChange(load);
    if (getCurrentInstance()) onScopeDispose(off);
    const activeKeys = computed(() => new Set(Object.entries(days.value).filter(([, v]) => v.entries > 0).map(([k]) => k)));
    const openTasks = computed(() => new Map(Object.entries(days.value).filter(([, v]) => v.open_tasks > 0).map(([k, v]) => [k, v.open_tasks])));
    return { days, activeKeys, openTasks, reload: load };
}

// --- składanie dnia ---

// Wszystko z jednego dnia: wpisy na osi, całodniowe (wydarzenia, terminy projektów, płatności), zadania bez
// godziny, kolejne dni cyklicznych i postęp zadań.
export function buildPlanerDay({ day, data, catalogs: cat = {} }) {
    const { buildEvents, splitTasks } = useV2Day();
    const key = ymd(day);
    if (!data) return { key, date: day, events: [], allDay: [], timed: [], unscheduled: [], repeatsWithoutTime: [], payments: [], progress: { done: 0, total: 0, overdue: 0 } };

    const tasks = splitTasks({ day, tasks: data.tasks ?? [], checklists: data.checklists ?? [], personDates: data.person_dates ?? [], cares: data.cares ?? [] });
    const events = buildEvents({
        day,
        mealEntries: planerMealEntries(data),
        workouts: data.workouts,
        sleepEntries: data.sleep_entries,
        dayReports: data.day_reports,
        mealsCatalog: cat.meals ?? [],
        taskEvents: tasks.events,
        products: cat.products ?? [],
        calendarEvents: data.calendar_events,
    });

    const repeats = (data.repeats ?? []).filter(r => r.date === key);
    for (const r of repeats) {
        if (!r.due_at) continue;
        events.push({
            id: `repeat-${r.uuid}-${r.date}`,
            kind: 'repeat',
            at: new Date(r.due_at),
            atLabel: hhmm(minutesOf(r.due_at)),
            timeLabel: hhmm(minutesOf(r.due_at)),
            title: r.text,
            meta: [],
            detail: r.checklist_title ?? t('planner.repeatRule.repeats'),
            repeat: r,
        });
    }
    events.sort((a, b) => a.at - b.at);

    const payments = (data.payments ?? []).filter(p => p.due_date === key).map(p => ({
        id: `payment-${p.plan?.uuid ?? p.loan?.uuid}-${p.period}`,
        kind: 'payment',
        allDay: true,
        title: p.name,
        amount: p.amount,
        status: p.status,
        payment: p,
    }));

    return {
        key,
        date: day,
        events,
        allDay: [...events.filter(e => e.allDay), ...payments],
        timed: events.filter(e => !e.allDay),
        unscheduled: tasks.unscheduled,
        repeatsWithoutTime: repeats.filter(r => !r.due_at),
        payments,
        progress: tasks.progress,
    };
}

// --- geometria osi ---

const POINT = 30;

// Początek i koniec wpisu na osi dnia (minuty od północy), przycięte do tej doby.
export const spanOf = (ev, key) => {
    const local = (value) => ({ key: ymd(new Date(value)), minutes: minutesOf(value) });
    const clip = (value, fallback) => {
        const l = local(value);
        if (l.key === key) return l.minutes;
        return l.key < key ? 0 : fallback;
    };
    let start = minutesOf(ev.at);
    let end = start + POINT;
    if (ev.kind === 'sleep' && ev.sleep?.wake_up_at) {
        start = ev.sleep.bedtime ? clip(ev.sleep.bedtime, 0) : Math.max(0, minutesOf(ev.sleep.wake_up_at) - 8 * 60);
        end = clip(ev.sleep.wake_up_at, 24 * 60);
    } else if (ev.kind === 'nap' && ev.nap?.end_at) {
        end = clip(ev.nap.end_at, 24 * 60);
    } else if (ev.kind === 'workout') {
        end = start + Math.max(POINT, Math.round((ev.workout?.duration_seconds ?? 45 * 60) / 60));
    } else if (ev.kind === 'event' && !ev.allDay && ev.calendarEvent) {
        start = clip(ev.calendarEvent.starts_at, 0);
        end = clip(ev.calendarEvent.ends_at, 24 * 60);
    }
    return { start, end: Math.min(24 * 60, Math.max(end, start + POINT)) };
};

// Godziny osi: z ustawień, rozciągnięte tak, żeby zmieścił się każdy wpis.
export const hourRange = (days, settingsValue, now = new Date()) => {
    let from = settingsValue.day_start ?? 6;
    let to = settingsValue.day_end ?? 23;
    // wieczorem dzisiejsza oś sięga do północy, żeby dało się planować do końca dnia
    if (days.some(d => d.key === ymd(now)) && now.getHours() * 60 + now.getMinutes() + 60 > to * 60) to = 24;
    for (const d of days) {
        for (const ev of d.timed) {
            if (ev.kind === 'sleep') continue; // noc zaczyna się przed osią — pokazujemy jej koniec
            const s = spanOf(ev, d.key);
            from = Math.min(from, Math.floor(s.start / 60));
            to = Math.max(to, Math.ceil(s.end / 60));
        }
    }
    return { from, to: Math.min(24, Math.max(to, from + 1)) };
};

// Bloki dnia z miejscem na osi; nakładające się dzielą szerokość, jak w kalendarzu.
export const layoutDay = (day, range) => {
    const span = (range.to - range.from) * 60;
    const blocks = day.timed.map((ev) => {
        const s = spanOf(ev, day.key);
        const top = Math.max(0, s.start - range.from * 60);
        const bottom = Math.min(span, s.end - range.from * 60);
        return { ev, top, height: Math.max(20, bottom - top), lane: 0, lanes: 1 };
    }).filter(b => b.top < span).sort((a, b) => a.top - b.top || b.height - a.height);

    let cluster = [];
    let clusterEnd = -1;
    const close = () => {
        const lanes = Math.max(0, ...cluster.map(b => b.lane)) + 1;
        for (const b of cluster) b.lanes = lanes;
        cluster = [];
    };
    for (const b of blocks) {
        if (b.top >= clusterEnd) { close(); clusterEnd = b.top + b.height; } else clusterEnd = Math.max(clusterEnd, b.top + b.height);
        const taken = new Set(cluster.filter(o => o.top + o.height > b.top).map(o => o.lane));
        while (taken.has(b.lane)) b.lane++;
        cluster.push(b);
    }
    close();

    return blocks.map(b => ({
        ...b,
        style: {
            top: `${(b.top / span) * 100}%`,
            height: `${(b.height / span) * 100}%`,
            left: `calc(${(b.lane / b.lanes) * 100}% + 2px)`,
            width: `calc(${100 / b.lanes}% - 4px)`,
        },
    }));
};

// Podgląd dnia pod datą: zadania do zrobienia, wydarzenia i wolny czas w godzinach osi
// (dziś — od teraz; dzień miniony nie ma wolnego czasu). Zajęte to wpisy, które biorą czas:
// wydarzenia, treningi, posiłki, zadania z godziną i drzemki.
const BUSY = ['event', 'workout', 'meal', 'meal-planned', 'task', 'nap'];
export const dayOverview = (day, settingsValue, now = new Date()) => {
    const todo = day.unscheduled.filter(t => !t.done).length
        + day.timed.filter(e => e.kind === 'task' && !e.task.done).length;
    const events = day.events.filter(e => e.kind === 'event').length;

    const todayKey = ymd(now);
    let free = null;
    if (day.key >= todayKey) {
        const end = (settingsValue.day_end ?? 23) * 60;
        let start = (settingsValue.day_start ?? 6) * 60;
        if (day.key === todayKey) start = Math.max(start, now.getHours() * 60 + now.getMinutes());
        const busy = day.timed.filter(e => BUSY.includes(e.kind)).map(e => spanOf(e, day.key))
            .map(s => [Math.max(s.start, start), Math.min(s.end, end)]).filter(([a, b]) => b > a)
            .sort((a, b) => a[0] - b[0]);
        let taken = 0;
        let cursor = start;
        for (const [a, b] of busy) {
            if (b <= cursor) continue;
            taken += b - Math.max(a, cursor);
            cursor = b;
        }
        free = Math.max(0, end - start - taken);
    }
    return { todo, events, free };
};

// „13 h 28 min" / „45 min" — niezaplanowany czas w podglądzie dnia.
export const freeLabel = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    if (h && m) return `${h} h ${m} min`;
    if (h) return `${h} h`;
    return `${m} min`;
};

// Co da się przeciągnąć: zadania, posiłki, treningi i wydarzenia z godziną.
export const isMovable = (ev) => ['task', 'meal', 'meal-planned', 'workout'].includes(ev.kind) || (ev.kind === 'event' && !ev.allDay);

// Zaległe zadania z całego planera, od najstarszego terminu.
export const sortOverdue = (list) => [...(list ?? [])].sort((a, b) => (taskDeadline(a)?.getTime() ?? 0) - (taskDeadline(b)?.getTime() ?? 0));

// Wpisy nowego Jedzenia (food_entries, gdy jest włączone) w kształcie starych wpisów posiłków,
// żeby oś, paski kalorii i raporty liczyły je tak samo; `food` niesie oryginał — zapis idzie
// wtedy do /food/entries. Wpis „nie zjedzone, było coś innego" nie trafia na oś.
export const foodAsMeal = (e) => ({
    uuid: e.uuid,
    name: [e.name, ...(e.choice_labels ?? []).map(l => l.toLowerCase())].join(' · '),
    planned_at: e.planned_at,
    status: e.status === 'eaten' ? 'eaten' : 'planned',
    ...Object.fromEntries(['calories', 'protein', 'carbs', 'fat', 'sugar', 'fiber', 'saturated_fat', 'salt', 'price'].map(k => [k, Math.round(e.values?.[k] ?? 0)])),
    ingredients: [],
    food: e,
});
export const planerMealEntries = (data) => [
    ...(data?.meal_entries ?? []),
    ...(data?.food_entries ?? []).filter(e => e.status !== 'skipped').map(foodAsMeal),
];

// Posiłki zaplanowane w dniach — źródła „Składniki na zakupy".
export const plannedMealSources = (data, keys) => (data?.meal_entries ?? [])
    .filter(e => e.status !== 'eaten' && keys.includes(ymd(new Date(e.planned_at))))
    .map(e => ({ kind: 'meal_entry', uuid: e.uuid }));
